import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { ArrowLeft, CalendarCheck, StickyNote, User, Clock, Loader2 } from "lucide-react";

export default function PromiseDetail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const fromTab = params.get("tab") || "overdue";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!code) return;
    api.get(`/collections/followup/${code}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [code]);

  const fu = data?.followup;
  const today = new Date().toISOString().slice(0, 10);
  const isToday = fu?.promiseDate === today;
  const isPast = fu?.promiseDate && fu.promiseDate < today;

  return (
    <Layout>
      <button type="button" onClick={() => navigate(`/collection?tab=${fromTab}`)} data-testid="promise-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to Collection Workflow
      </button>

      <PageHeader title="Promise to Pay" subtitle={`Record for ${code}`} />

      {loading && <div className="flex items-center gap-2 text-slate-400 py-12"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>}

      {!loading && data && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 max-w-lg" data-testid="promise-card">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center"><User className="h-5 w-5 text-white" /></div>
            <div>
              <Link to={`/customer?q=${encodeURIComponent(data.customerName || "")}`} className="font-heading font-bold text-slate-900 hover:underline">{data.customerName}</Link>
              <div className="text-xs text-slate-400 font-mono-fig">{data.loanCode} · due day {data.dueDay || "—"} · outstanding {fmtRM(data.outstanding)}</div>
            </div>
          </div>

          {fu && fu.status === "Promised to Pay" ? (
            <div className="pt-5 space-y-5">
              <div className="flex items-start gap-3">
                <CalendarCheck className="h-5 w-5 text-violet-600 mt-0.5" />
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Promised to pay on</div>
                  <div data-testid="promise-date" className="font-mono-fig text-lg font-bold text-slate-900">{fu.promiseDate || "No date set"}</div>
                  {isToday && <span className="inline-block mt-1 text-[11px] font-bold uppercase bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">Due today</span>}
                  {isPast && <span className="inline-block mt-1 text-[11px] font-bold uppercase bg-rose-100 text-rose-700 rounded px-1.5 py-0.5">Promise overdue</span>}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <StickyNote className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Note</div>
                  <div data-testid="promise-note" className="text-sm text-slate-700">{fu.note || "—"}</div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Recorded by</div>
                  <div className="text-sm text-slate-700">{fu.updatedBy || "—"}{fu.updatedAt ? ` · ${new Date(fu.updatedAt).toLocaleString()}` : ""}</div>
                </div>
              </div>

              <Link to={`/collection/action?code=${encodeURIComponent(code)}&tab=${fromTab}`} className="block text-center bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2.5 text-sm font-medium">Update Action</Link>
            </div>
          ) : (
            <div className="pt-5 text-sm text-slate-400">No active promise-to-pay record for this loan this month.</div>
          )}
        </div>
      )}
    </Layout>
  );
}
